export const VOICE_CHAT_SUBPROTOCOL = 'cobli.voice.v1';
export const VOICE_CHAT_PING_INTERVAL_MS = 25_000;
export const VOICE_CHAT_CONNECT_TIMEOUT_MS = 15_000;

const NETWORK_DROP_CLOSE_CODE = 1006;

const SERVER_ERROR_REASONS = ['device_busy', 'camera_offline', 'invalid_device', 'unsupported_device', 'internal_error'] as const;

export type VoiceChatServerErrorReason = (typeof SERVER_ERROR_REASONS)[number];
export type VoiceChatErrorReason = VoiceChatServerErrorReason | 'connection_failed' | 'connect_timeout';
export type VoiceChatEndReason = 'local' | 'server' | 'network';

interface VoiceChatServerMessage {
  type?: string;
  value?: string;
  reason?: string;
}

export interface VoiceChatSocketCallbacks {
  onAccepted: () => void;
  /** Fired at most once, only when the call never reached (or never left) the accepted state. */
  onError: (reason: VoiceChatErrorReason) => void;
  /** Fired at most once, only after the call was accepted. */
  onEnded: (reason: VoiceChatEndReason) => void;
}

interface VoiceChatSocketOptions {
  host: string;
  getToken: () => Promise<string | null>;
  /** Test seam — defaults to the native WebSocket constructor. */
  createWebSocket?: (url: string, protocols: string[]) => WebSocket;
}

const toServerErrorReason = (reason?: string): VoiceChatErrorReason => {
  return SERVER_ERROR_REASONS.includes(reason as VoiceChatServerErrorReason) ? (reason as VoiceChatServerErrorReason) : 'internal_error';
};

/**
 * Manages one voice-chat call over WebSocket against the pooler:
 * connecting the socket IS opening the call on the vendor; closing it IS hanging up.
 * V1 carries no audio — binary frames are a future extension point (pooler phase 4).
 */
export class VoiceChatSocket {
  private readonly options: VoiceChatSocketOptions;
  private ws: WebSocket | null = null;
  private callbacks: VoiceChatSocketCallbacks | null = null;
  private accepted = false;
  private settled = false;
  /** `disconnect()` chegou antes do socket existir — ver a checagem pós-`getToken()` em `connect`. */
  private aborted = false;
  private serverErrorReason: VoiceChatErrorReason | null = null;
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private connectTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(options: VoiceChatSocketOptions) {
    this.options = options;
  }

  public connect = async (deviceId: string, callbacks: VoiceChatSocketCallbacks): Promise<void> => {
    this.callbacks = callbacks;
    const token = await this.options.getToken();

    // A renovação de token é assíncrona: fechar o dialog, desligar, desmontar ou trocar de device
    // nessa janela deve cancelar a tentativa. Abrir o socket aqui abriria a chamada no vendor sem
    // UI para encerrá-la, e o ping manteria a sessão viva — o device ficaria preso ocupado.
    if (this.aborted) {
      return;
    }

    if (!token) {
      this.settle(() => callbacks.onError('connection_failed'));
      return;
    }

    const wsHost = this.options.host.replace(/^http/, 'ws');
    const url = `${wsHost}/cameras/voice/devices/${deviceId}`;
    const createWebSocket = this.options.createWebSocket ?? ((wsUrl, protocols) => new WebSocket(wsUrl, protocols));

    try {
      this.ws = createWebSocket(url, [VOICE_CHAT_SUBPROTOCOL, token]);
    } catch {
      this.settle(() => callbacks.onError('connection_failed'));
      return;
    }

    // Limita a espera pelo `accepted` a 15s: o vendor tem uma janela de ~30s para atender, e sem
    // esse corte o socket ficaria aberto indefinidamente com a UI presa em "conectando".
    this.connectTimeout = setTimeout(this.handleConnectTimeout, VOICE_CHAT_CONNECT_TIMEOUT_MS);
    this.ws.onmessage = this.handleMessage;
    this.ws.onclose = this.handleClose;
  };

  public disconnect = (): void => {
    this.aborted = true;
    if (this.settled && !this.ws) {
      return;
    }
    const wasAccepted = this.accepted;
    const ws = this.ws;
    this.teardown();
    ws?.close(1000);
    if (wasAccepted) {
      this.settle(() => this.callbacks?.onEnded('local'));
    } else {
      this.settled = true;
    }
  };

  public isActive = (): boolean => {
    return this.ws !== null && !this.settled;
  };

  private handleConnectTimeout = () => {
    const ws = this.ws;
    this.teardown();
    ws?.close(1000);
    this.settle(() => this.callbacks?.onError('connect_timeout'));
  };

  private handleMessage = (event: MessageEvent) => {
    let message: VoiceChatServerMessage;
    try {
      message = JSON.parse(event.data as string);
    } catch {
      return;
    }

    if (message.type === 'state' && message.value === 'accepted') {
      if (this.connectTimeout) {
        clearTimeout(this.connectTimeout);
        this.connectTimeout = null;
      }
      this.accepted = true;
      this.pingInterval = setInterval(this.sendPing, VOICE_CHAT_PING_INTERVAL_MS);
      this.callbacks?.onAccepted();
      return;
    }

    if (message.type === 'error') {
      this.serverErrorReason = toServerErrorReason(message.reason);
    }
  };

  private handleClose = (event: CloseEvent) => {
    const wasAccepted = this.accepted;
    const serverErrorReason = this.serverErrorReason;
    this.teardown();
    this.ws = null;

    if (serverErrorReason) {
      this.settle(() => this.callbacks?.onError(serverErrorReason));
      return;
    }
    if (!wasAccepted) {
      this.settle(() => this.callbacks?.onError('connection_failed'));
      return;
    }
    this.settle(() => this.callbacks?.onEnded(event.code === NETWORK_DROP_CLOSE_CODE ? 'network' : 'server'));
  };

  private sendPing = () => {
    this.ws?.send(JSON.stringify({ type: 'ping' }));
  };

  private settle = (notify: () => void) => {
    if (this.settled) {
      return;
    }
    this.settled = true;
    notify();
  };

  private teardown = () => {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    if (this.connectTimeout) {
      clearTimeout(this.connectTimeout);
      this.connectTimeout = null;
    }
    if (this.ws) {
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws = null;
    }
  };
}

export default VoiceChatSocket;
